import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminGrowthSnapshotSectionsListAction,
  adminGrowthSnapshotStaticScreenCreateAction,
  adminGrowthSnapshotStaticScreenGetAction,
  adminGrowthSnapshotStaticScreenUpdateAction,
  adminGrowthSnapshotsListAction
} from "../Action/action";

const SCREEN_KEYS = [
  { value: "beginning_intro", label: "Beginning intro (Welcome)" },
  { value: "end_intro", label: "End intro (Welcome Back)" },
  { value: "welcome", label: "Welcome (journey screen 1)" },
  { value: "one_final_thought", label: "One Final Thought" },
  { value: "keep_going", label: "Keep Going" }
];

export default function StaticScreenForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { screenId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [snapshots, setSnapshots] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    growth_snapshot_id: searchParams.get("snapshot_id") || "",
    section_id: searchParams.get("section_id") || "",
    screen_key: "welcome",
    heading: "",
    subheading: "",
    body_text: "",
    primary_button_label: "Continue",
    status: 1
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotsListAction({ page: 1, per_page: 100 });
        if (!cancelled) setSnapshots(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!form.growth_snapshot_id) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotSectionsListAction(form.growth_snapshot_id, {});
        if (cancelled) return;
        setSections(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setSections([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.growth_snapshot_id]);

  useEffect(() => {
    if (!isEdit || !screenId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminGrowthSnapshotStaticScreenGetAction(screenId);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          toast.error(res?.message || "Not found", { toastId: "gs-st-form" });
          navigate("/reflections-screens/static-screens");
          return;
        }
        const d = res.data;
        setForm({
          growth_snapshot_id: String(d.growth_snapshot_id || ""),
          section_id: d.section_id != null ? String(d.section_id) : "",
          screen_key: d.screen_key || "welcome",
          heading: d.heading || "",
          subheading: d.subheading || "",
          body_text: d.body_text || "",
          primary_button_label: d.primary_button_label || "",
          status: Number(d.status) === 0 ? 0 : 1
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to load", {
          toastId: "gs-st-form"
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, screenId, navigate]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const listPath = form.growth_snapshot_id
    ? `/reflections-screens/static-screens?snapshot_id=${form.growth_snapshot_id}`
    : "/reflections-screens/static-screens";

  const onSnapshotChange = (value) => {
    setForm((prev) => ({
      ...prev,
      growth_snapshot_id: value,
      section_id: ""
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.growth_snapshot_id) {
      toast.error("Select a growth snapshot", { toastId: "gs-st-form" });
      return;
    }
    if (!String(form.screen_key || "").trim()) {
      toast.error("Screen key is required", { toastId: "gs-st-form" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        screen_key: form.screen_key.trim(),
        section_id: form.section_id ? Number(form.section_id) : null,
        heading: form.heading || null,
        subheading: form.subheading || null,
        body_text: form.body_text || null,
        primary_button_label: form.primary_button_label || null,
        status: Number(form.status) === 1 ? 1 : 0
      };
      const res = isEdit
        ? await adminGrowthSnapshotStaticScreenUpdateAction(screenId, payload)
        : await adminGrowthSnapshotStaticScreenCreateAction(form.growth_snapshot_id, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Updated" : "Created", { toastId: "gs-st-form" });
      navigate(listPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "gs-st-form"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/reflections-screens">Reflections & screens</Link>
          <span>›</span>
          <Link to={listPath}>Static screens</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"}</span>
        </div>
        <Link className="btn btn-outline btn-sm" to={listPath}>
          ← Back
        </Link>
      </div>

      <form onSubmit={onSubmit}>
        <div className="card mb-6">
          <h2 className="section-title">{isEdit ? "Edit static screen" : "Add static screen"}</h2>

          <div className="form-row">
            <div className="form-group">
              <label>
                Growth snapshot <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={form.growth_snapshot_id}
                disabled={isEdit}
                onChange={(e) => onSnapshotChange(e.target.value)}
                required
              >
                <option value="">Select…</option>
                {snapshots.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.title || `Snapshot #${s.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Section</label>
              <select
                className="form-control"
                value={form.section_id}
                disabled={!form.growth_snapshot_id}
                onChange={(e) => setField("section_id", e.target.value)}
              >
                <option value="">Snapshot-level (no section)</option>
                {sections.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.title || `Section #${s.id}`}
                    {s.phase ? ` · ${s.phase}` : ""}
                  </option>
                ))}
              </select>
              <p className="form-hint" style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                Optional. Use for intro screens tied to a beginning/end/Looking Back section. Leave blank for
                journey-wide screens (Welcome / One Final Thought / Keep Going).
              </p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Screen key</label>
              <select
                className="form-control"
                value={form.screen_key}
                disabled={isEdit}
                onChange={(e) => setField("screen_key", e.target.value)}
              >
                {SCREEN_KEYS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Heading</label>
              <input
                className="form-control"
                value={form.heading}
                onChange={(e) => setField("heading", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Subheading</label>
              <input
                className="form-control"
                value={form.subheading}
                onChange={(e) => setField("subheading", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Primary button label</label>
              <input
                className="form-control"
                value={form.primary_button_label}
                onChange={(e) => setField("primary_button_label", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setField("status", Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Body text</label>
            <textarea
              className="form-control"
              rows={6}
              value={form.body_text}
              onChange={(e) => setField("body_text", e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to={listPath}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
